'use client'

import React, { Suspense, useCallback, useRef, useState } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import { useAuthInfo } from '@sweetauth/core'

import { Skeleton } from '@/components/common/Skeleton'

import { fetchRecipientRecentAwards } from '@/context/usaspending'

import { fetchRecipientAwards, usaspendingKeys } from '@/hooks/usaspending'

import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { Currency, DateDisplay } from '../../Formatters'
import { SectionCard } from '../../shared'
import { TableUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

const AllAwardsModal = dynamic(
  () => import('./AllAwardsModal').then((m) => m.AllAwardsModal),
  { ssr: false },
)

/**
 * RecipientDetail.RecentAwards
 *
 * Table of recent prime awards with links to award detail pages.
 * Full-width section at the bottom of the dashboard.
 */
export function RecentAwardsSection() {
  const { recipientId } = useRecipientDetail()
  const queryClient = useQueryClient()
  const { accessToken } = useAuthInfo()
  const prefetched = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (prefetched.current || !accessToken) return
    prefetched.current = true
    const limit = 25
    queryClient.prefetchInfiniteQuery({
      queryKey: usaspendingKeys.recipientAwardList(recipientId, {
        limit,
        category: undefined,
      }),
      queryFn: () =>
        fetchRecipientAwards(accessToken, recipientId, {
          page: 1,
          limit,
          sort: 'action_date',
          order: 'desc',
        }),
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
    })
  }, [queryClient, accessToken, recipientId])

  return (
    <SectionCard
      id="recent-awards"
      title="Recent Prime Awards"
      fullWidth
      animationDelay={450}
      onMouseEnter={handleMouseEnter}
    >
      <QueryErrorBoundary
        title="Failed to load awards"
        serviceUnavailableFallback={<TableUnavailable />}
      >
        <Suspense fallback={<RecentAwardsSkeleton />}>
          <RecentAwardsContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function RecentAwardsContent({ id }: { id: string }) {
  const { accessToken } = useAuthInfo()
  const token = accessToken ?? ''
  const { data: awards } = useQuery({
    queryKey: usaspendingKeys.recipientRecentAwards(id, 5),
    queryFn: () => fetchRecipientRecentAwards(token, id, 5),
    staleTime: 7 * 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const [showAll, setShowAll] = useState(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('view') === 'awards'
  })

  const toggleShowAll = useCallback((open: boolean) => {
    setShowAll(open)
    const params = new URLSearchParams(window.location.search)
    if (open) {
      params.set('view', 'awards')
    } else {
      params.delete('view')
    }
    const qs = params.toString()
    window.history.replaceState(
      null,
      '',
      qs ? `?${qs}` : window.location.pathname,
    )
  }, [])

  if (!awards) {
    return <RecentAwardsSkeleton />
  }

  if (awards.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-primary-500 dark:text-primary-400">
        No recent awards found for this recipient.
      </p>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-primary-200 dark:border-primary-700">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-primary-200 bg-primary-50 text-xs font-semibold uppercase text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
          <tr>
            <th className="px-4 py-3">Award ID</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Agency</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
          {awards.map((award, index) => (
            <tr
              key={award.id}
              className="bg-white transition-colors hover:bg-primary-50 dark:bg-primary-900 dark:hover:bg-primary-800"
              style={{
                animation: `fadeInRow 200ms ease-out ${index * 30}ms backwards`,
              }}
            >
              <td className="px-4 py-3">
                <Link
                  href={`/awards/usaspending/${award.id}`}
                  className="font-mono font-medium text-accent-500 hover:underline dark:text-accent-400"
                >
                  {award.piid}
                </Link>
              </td>
              <td className="px-4 py-3 text-primary-600 dark:text-primary-400">
                <DateDisplay dateStr={award.date} />
              </td>
              <td className="px-4 py-3 text-primary-600 dark:text-primary-400">
                {award.agency}
              </td>
              <td className="px-4 py-3">
                <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300">
                  {award.type}
                </span>
              </td>
              <td className="px-4 py-3 text-right font-medium tabular-nums text-primary-900 dark:text-primary-100">
                <Currency value={award.amount} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* View all button */}
      <div className="border-t border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-700 dark:bg-primary-800/50">
        <button
          type="button"
          onClick={() => toggleShowAll(true)}
          className="text-sm font-medium text-accent-500 hover:underline dark:text-accent-400"
        >
          View all awards →
        </button>
      </div>

      <AllAwardsModal
        recipientHash={id}
        open={showAll}
        onOpenChange={toggleShowAll}
      />

      <style jsx global>{`
        @keyframes fadeInRow {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}

function RecentAwardsSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-primary-200 dark:border-primary-700">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-primary-200 bg-primary-50 text-xs font-semibold uppercase text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
          <tr>
            <th className="px-4 py-3">Award ID</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Agency</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
          {[1, 2, 3].map((i) => (
            <tr key={i} className="bg-white dark:bg-primary-900">
              <td className="px-4 py-3">
                <Skeleton className="h-5 w-24" />
              </td>
              <td className="px-4 py-3">
                <Skeleton className="h-5 w-24" />
              </td>
              <td className="px-4 py-3">
                <Skeleton className="h-5 w-36" />
              </td>
              <td className="px-4 py-3">
                <Skeleton className="h-5 w-20" />
              </td>
              <td className="px-4 py-3 text-right">
                <Skeleton className="ml-auto h-5 w-24" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
